/**
 * Score celebrations (UX-SPEC §5 "Submitting", §6): the points float up from
 * the word and fly into the scorer's player card; ≥ 25 adds a sparkle burst;
 * ≥ 40 or a bingo adds confetti and a big "BINGO +50" / "+56!" flourish; a
 * win throws confetti from both sides. Reduced motion: the float just fades
 * in place, no sparks, no confetti.
 *
 * Events are queued in uiStore by the actions/sync layers and consumed here,
 * so the effect runs once even if this component re-renders.
 *
 * Confetti draws on our own aria-hidden canvas inside the (aria-hidden)
 * celebration layer, not on the library's default canvas appended straight to
 * <body> outside every landmark.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import confetti from 'canvas-confetti';
import { BINGO_BONUS } from '@wordloom/engine';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useUiStore, type CelebrationEvent } from '../state/uiStore';
import './Celebration.css';

interface Point {
  x: number;
  y: number;
}

interface Effect {
  id: string;
  kind: CelebrationEvent['kind'];
  score: number;
  from: Point;
  to: Point | null;
  /** Big centred flourish text, if any. */
  flourish: string | null;
}

const FLOAT_MS = 1250;
const FLOURISH_MS = 1700;
/** Reduced motion: fades in and out take at most this long (UX-SPEC §6). */
const REDUCED_FADE_MS = 120;

/** Keyframe times for fade in → hold → fade out over `totalMs`, fades ≤ 120 ms. */
function fadeTimes(totalMs: number): number[] {
  const f = REDUCED_FADE_MS / totalMs;
  return [0, f, 1 - f, 1];
}

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function centre(r: DOMRect): Point {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** Centre of the union of the scored squares (falls back to the board centre). */
function originFor(cells: CelebrationEvent['cells']): Point {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const c of cells) {
    const el = document.querySelector(`[data-cell="${c.row},${c.col}"]`);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (Number.isFinite(left)) return { x: (left + right) / 2, y: (top + bottom) / 2 };
  const board = document.querySelector('.board[role="grid"]');
  if (board) return centre(board.getBoundingClientRect());
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

/** The visible player card/chip for a seat (desktop has cards, smaller layouts chips). */
function targetFor(seat: 0 | 1): Point | null {
  const els = Array.from(document.querySelectorAll(`[data-player-card="${seat}"]`)).filter(visible);
  const el = els[0];
  return el ? centre(el.getBoundingClientRect()) : null;
}

/** Theme colours for confetti, read from the live tokens. */
function palette(): string[] {
  const css = getComputedStyle(document.documentElement);
  const names = ['--gold', '--accent', '--tw', '--tl', '--good', '--dw'];
  const colours = names.map((n) => css.getPropertyValue(n).trim()).filter(Boolean);
  return colours.length > 0 ? colours : ['#E8A93A', '#4B5BD7', '#BC5037', '#3D78A8'];
}

const CONFETTI_Z = 960; // above the game-over overlay (900), below toasts

type Cannon = confetti.CreateTypes;

/**
 * One confetti instance per canvas. A worker keeps the animation off the main
 * thread like the library default; the library marks a canvas it has handed
 * to the worker, so a StrictMode re-run cannot transfer it twice.
 */
const cannons = new WeakMap<HTMLCanvasElement, Cannon>();
function cannonFor(canvas: HTMLCanvasElement): Cannon {
  let cannon = cannons.get(canvas);
  if (!cannon) {
    cannon = confetti.create(canvas, { resize: true, useWorker: true, disableForReducedMotion: true });
    cannons.set(canvas, cannon);
  }
  return cannon;
}

function burstAt(fire: Cannon, p: Point, big: boolean): void {
  const origin = { x: p.x / window.innerWidth, y: p.y / window.innerHeight };
  void fire({
    particleCount: big ? 120 : 70,
    spread: big ? 90 : 70,
    startVelocity: big ? 42 : 32,
    ticks: 180,
    scalar: 0.9,
    origin,
    colors: palette(),
    disableForReducedMotion: true,
  });
}

function winConfetti(fire: Cannon): void {
  const colors = palette();
  const shot = (x: number, angle: number) =>
    void fire({
      particleCount: 90,
      angle,
      spread: 65,
      startVelocity: 55,
      ticks: 240,
      origin: { x, y: 0.75 },
      colors,
      disableForReducedMotion: true,
    });
  shot(0.05, 60);
  shot(0.95, 120);
  window.setTimeout(() => {
    shot(0.15, 70);
    shot(0.85, 110);
  }, 380);
}

function flourishFor(e: CelebrationEvent): string | null {
  if (e.kind === 'bingo') return `BINGO +${BINGO_BONUS}`;
  if (e.kind === 'big') return `+${e.score}!`;
  return null;
}

/** Eight sparks radiating from the word (score ≥ CELEBRATE_GOOD_MOVE). */
function Sparks({ at }: { at: Point }) {
  const sparks = Array.from({ length: 10 }, (_, i) => (i / 10) * Math.PI * 2);
  return (
    <span className="sparks" style={{ left: at.x, top: at.y }} aria-hidden="true">
      {sparks.map((a, i) => (
        <motion.i
          key={i}
          className="sparks__spark"
          initial={{ x: 0, y: 0, scale: 0.4, opacity: 1 }}
          animate={{ x: Math.cos(a) * 56, y: Math.sin(a) * 56, scale: 1, opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        />
      ))}
    </span>
  );
}

function ScoreFloat({ effect, reduced }: { effect: Effect; reduced: boolean }) {
  const { from, to } = effect;
  const big = effect.kind === 'big' || effect.kind === 'bingo';
  if (reduced || !to) {
    return (
      <motion.span
        className="score-float"
        style={{ left: from.x, top: from.y }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ duration: FLOAT_MS / 1000, times: reduced ? fadeTimes(FLOAT_MS) : [0, 0.1, 0.75, 1] }}
      >
        +{effect.score}
      </motion.span>
    );
  }
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return (
    <motion.span
      className={big ? 'score-float score-float--big' : 'score-float'}
      style={{ left: from.x, top: from.y }}
      initial={{ x: 0, y: 8, scale: 0.6, opacity: 0 }}
      animate={{
        x: [0, 0, dx],
        y: [8, -26, dy],
        scale: [0.6, big ? 1.35 : 1.15, 0.55],
        opacity: [0, 1, 0.9, 0],
      }}
      transition={{
        duration: FLOAT_MS / 1000,
        times: [0, 0.32, 1],
        ease: [0.3, 0.7, 0.2, 1],
        opacity: { duration: FLOAT_MS / 1000, times: [0, 0.12, 0.85, 1] },
      }}
    >
      +{effect.score}
    </motion.span>
  );
}

export function Celebration() {
  const queue = useUiStore((s) => s.celebrations);
  const reduced = useReducedMotion();
  const [effects, setEffects] = useState<Effect[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (queue.length === 0) return;
    const ui = useUiStore.getState();
    const canvas = canvasRef.current;
    const fire = canvas && !reduced ? cannonFor(canvas) : null;
    const added: Effect[] = [];
    for (const e of queue) {
      ui.consumeCelebration(e.id);
      if (e.kind === 'win') {
        if (fire) winConfetti(fire);
        continue;
      }
      const from = originFor(e.cells);
      const effect: Effect = {
        id: e.id,
        kind: e.kind,
        score: e.score,
        from,
        to: targetFor(e.seat),
        flourish: flourishFor(e),
      };
      added.push(effect);
      if (fire && (e.kind === 'big' || e.kind === 'bingo')) burstAt(fire, from, e.kind === 'bingo');
    }
    if (added.length === 0) return;
    setEffects((list) => [...list, ...added]);
    const ids = new Set(added.map((a) => a.id));
    window.setTimeout(() => setEffects((list) => list.filter((f) => !ids.has(f.id))), FLOURISH_MS + 200);
  }, [queue, reduced]);

  // Always mounted (it is inert and hidden from assistive tech): a win's
  // confetti needs the canvas even when there is no score float.
  return createPortal(
    <div className="celebration" aria-hidden="true" style={{ zIndex: CONFETTI_Z }}>
      <AnimatePresence>
        {effects.map((f) => (
          <span key={f.id}>
            <ScoreFloat effect={f} reduced={reduced} />
            {!reduced && f.kind !== 'score' && <Sparks at={f.from} />}
            {f.flourish && (
              <motion.span
                className="flourish"
                initial={{ opacity: 0, scale: reduced ? 1 : 0.5, rotate: reduced ? 0 : -6 }}
                animate={{ opacity: [0, 1, 1, 0], scale: reduced ? 1 : [0.5, 1.12, 1, 1.04], rotate: 0 }}
                transition={{ duration: FLOURISH_MS / 1000, times: reduced ? fadeTimes(FLOURISH_MS) : [0, 0.18, 0.75, 1] }}
              >
                {f.flourish}
              </motion.span>
            )}
          </span>
        ))}
      </AnimatePresence>
      {/* Last, so the confetti falls in front of the floats. */}
      <canvas ref={canvasRef} className="celebration__confetti" aria-hidden="true" />
    </div>,
    document.body,
  );
}
